// ─────────────────────────────────────────────
// KLOTHIQ — Sale Tracking Tab (FEAT-SALE-STATUS-003)
// Reads the existing audit_logs trail for one sale — no new table.
// ─────────────────────────────────────────────

import { History, Clock } from 'lucide-react';
import { useSaleTracking, type SaleTrackingEntry } from '@/hooks/useSales';
import { getStatusBadge } from '@klothiq/design-tokens';
import LoadingState from '@/components/common/LoadingState';
import ErrorState from '@/components/common/ErrorState';
import SaleTrackingStepper from './SaleTrackingStepper';

const DOT_CLASSES = {
  green:  'bg-emerald-400',
  amber:  'bg-amber-400',
  red:    'bg-red-400',
  blue:   'bg-cyan-400',
  purple: 'bg-purple-400',
  grey:   'bg-slate-400',
} as const;

function formatTimestamp(iso: string) {
  return new Date(iso).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit', month: 'short', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

// Each audit_logs row's `metadata`/`old_data`/`new_data` shape tells us
// which track changed — the three status-changing RPCs write distinct
// shapes (see change_sale_status / request_sale_return / decide_sale_return
// / process_sale_return in migrations 00035/00036). Payment rows are
// deliberately NOT sourced from here — record_payment audits table_name
// = 'payments', not 'sales'; Payment History stays its own section.
function describeEntry(entry: SaleTrackingEntry): { text: string; statusKey: string } | null {
  const meta = entry.metadata ?? {};
  const newData = entry.new_data ?? {};

  // Return completed — process_sale_return's audit write
  if (meta.type === 'return' && typeof meta.total_returned === 'number') {
    const n = meta.total_returned as number;
    return { text: `Return completed — ${n} item${n === 1 ? '' : 's'} restocked`, statusKey: 'return_completed' };
  }

  // Return requested / accepted / rejected — carry return_status in new_data
  if (typeof newData.return_status === 'string') {
    const status = newData.return_status as string;
    if (status === 'requested') {
      const itemCount = Array.isArray(newData.items) ? newData.items.length : null;
      const reason = typeof newData.reason === 'string' ? newData.reason : null;
      const parts = [`Return requested`, itemCount != null ? `(${itemCount} item${itemCount === 1 ? '' : 's'})` : null];
      return {
        text: [parts.filter(Boolean).join(' '), reason ? `— "${reason}"` : null].filter(Boolean).join(' '),
        statusKey: 'return_requested',
      };
    }
    if (status === 'accepted') return { text: 'Return accepted', statusKey: 'return_accepted' };
    if (status === 'rejected') {
      const note = typeof newData.note === 'string' ? newData.note : null;
      return { text: ['Return rejected', note ? `— "${note}"` : null].filter(Boolean).join(' '), statusKey: 'return_rejected' };
    }
  }

  // Delivery status change — change_sale_status's audit write
  if (typeof meta.from === 'string' && typeof meta.to === 'string') {
    return {
      text: `Delivery status changed: ${getStatusBadge(meta.from as string).label} → ${getStatusBadge(meta.to as string).label}`,
      statusKey: meta.to as string,
    };
  }

  return null;
}

interface SaleTrackingTabProps {
  saleId: string;
  saleStatus?: string;
  returnStatus?: string | null;
}

export default function SaleTrackingTab({
  saleId,
  saleStatus = 'draft',
  returnStatus = null,
}: SaleTrackingTabProps) {
  const { data: entries, isLoading, error, refetch } = useSaleTracking(saleId);

  if (isLoading) return <LoadingState message="Loading tracking history…" />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} />;

  const rows = (entries ?? [])
    .map((entry) => ({ entry, described: describeEntry(entry) }))
    .filter((r) => r.described !== null) as { entry: SaleTrackingEntry; described: { text: string; statusKey: string } }[];

  return (
    <div className="space-y-4">
      {/* Visual Stepper */}
      <SaleTrackingStepper
        saleStatus={saleStatus}
        returnStatus={returnStatus}
        trackingEntries={entries ?? []}
      />

      {/* Audit Event Timeline */}
      <div className="obsidian-surface overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="border-b border-border px-4 py-3">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <History className="h-4 w-4 text-muted-foreground" /> Audit Log Event Timeline
          </h3>
        </div>
        {rows.length === 0 ? (
          <div className="p-6 text-center text-xs text-muted-foreground">
            No milestone status changes recorded yet.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map(({ entry, described }) => {
              const color = getStatusBadge(described.statusKey).color;
              return (
                <li key={entry.id} className="flex gap-3 px-4 py-3">
                  <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[color]}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-foreground">{described.text}</p>
                    <p className="mt-0.5 flex items-center gap-1 text-xs text-muted-foreground">
                      {entry.actor_name ?? 'Unknown user'} · <Clock className="h-3 w-3" /> {formatTimestamp(entry.created_at)}
                    </p>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
